<script setup>
import { useAlert } from 'dashboard/composables';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import ConversationResolutionSubjectModal from './component/ConversationResolutionSubjectModal.vue';
import ConversationResolutionSubjectTableBody from './component/ConversationResolutionSubjectTableBody.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const { t } = useI18n();

const showSubjectModal = ref(false);
const subjectModalMode = ref('add');
const selectedSubject = ref(null);
const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});

const records = useMapGetter(
  'conversationResolutionSubject/getConversationResolutionSubjects'
);
const uiFlags = useMapGetter('conversationResolutionSubject/getUIFlags');

const deleteConfirmText = computed(
  () =>
    `${t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.CONFIRM.YES')} ${activeResponse.value.name}`
);

const deleteRejectText = computed(
  () =>
    `${t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.CONFIRM.NO')} ${activeResponse.value.name}`
);

const deleteMessage = computed(() => ` ${activeResponse.value.name} ? `);

const fetchSubjects = async () => {
  try {
    await store.dispatch(
      'conversationResolutionSubject/getConversationResolutionSubject'
    );
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchSubjects();
});

const tableHeaders = computed(() => [
  t('CONVERSATION_RESOLUTION_SUBJECT.LIST.TABLE_HEADER.NAME'),
  t('CONVERSATION_RESOLUTION_SUBJECT.LIST.TABLE_HEADER.ACTIONS'),
]);

const showAlertMessage = message => {
  loading.value[activeResponse.value.id] = false;
  activeResponse.value = {};
  useAlert(message);
};

const openAddModal = () => {
  subjectModalMode.value = 'add';
  selectedSubject.value = null;
  showSubjectModal.value = true;
};

const openEditModal = subject => {
  subjectModalMode.value = 'edit';
  selectedSubject.value = subject;
  showSubjectModal.value = true;
};

const hideSubjectModal = () => {
  selectedSubject.value = null;
  showSubjectModal.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

// A subject in use by existing resolutions is disabled server-side (kept, hidden from
// the list) rather than deleted; an unused subject is removed. Both return success and
// drop the row from the list, so a single success message covers both outcomes.
const deleteSubject = async id => {
  try {
    await store.dispatch(
      'conversationResolutionSubject/deleteConversationResolutionSubject',
      id
    );
    showAlertMessage(
      t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.API.SUCCESS_MESSAGE')
    );
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading.value[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteSubject(activeResponse.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('CONVERSATION_RESOLUTION_SUBJECT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CONVERSATION_RESOLUTION_SUBJECT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CONVERSATION_RESOLUTION_SUBJECT.HEADER')"
        :description="$t('CONVERSATION_RESOLUTION_SUBJECT.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            icon="i-lucide-circle-plus"
            :label="$t('CONVERSATION_RESOLUTION_SUBJECT.HEADER_BTN_TXT')"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table
        class="min-w-full overflow-x-auto divide-y divide-slate-75 dark:divide-slate-700"
      >
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 pr-4 font-semibold text-left text-slate-700 dark:text-slate-300"
          >
            <span class="mb-0">{{ thHeader }}</span>
          </th>
        </thead>

        <ConversationResolutionSubjectTableBody
          :subjects="records"
          :loading="loading"
          @edit="openEditModal"
          @delete="openDeletePopup"
        />
      </table>
    </template>

    <woot-modal v-model:show="showSubjectModal" :on-close="hideSubjectModal">
      <ConversationResolutionSubjectModal
        :mode="subjectModalMode"
        :selected-subject="selectedSubject"
        @close="hideSubjectModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.CONFIRM.TITLE')"
      :message="$t('CONVERSATION_RESOLUTION_SUBJECT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
